import { isNumber } from '_internal/lang/isNumber';
import type { AnimationKeyframeWithOptions } from '../hooks/types';
import type { AnimationKeyframe, TransformShorthands } from '../types';

type TransformStyleOptions = TransformShorthands;

/**
 * Convert transform shorthands into an array of transform shorthands
 *
 * @example
 * const transforms = getTransformStyle({ x: 0, scale: 0});
 * // ['translate3d(0, 0, 0)', 'scale(0)'];
 *
 * @todo (Vapor-544): ensure correct transform order is applied
 */

export function getTransformStyle({
  x,
  y,
  z,
  scale,
  scaleX,
  scaleY,
  rotate,
}: TransformStyleOptions) {
  const transforms = [];

  if ([x, y, z].some((v) => v !== undefined)) {
    transforms.push(
      // @ts-expect-error FIXME(strictNullChecks)
      `translate3d(${[x, y, z].map((v) => addUnit(v, 'px')).join(',')})`
    );
  }

  if ([scale, scaleX, scaleY].some((v) => v !== undefined)) {
    if (isNumber(scale)) {
      transforms.push(`scale(${scale})`);
    } else {
      transforms.push(
        // @ts-expect-error FIXME(strictNullChecks)
        `scale(${[scaleX, scaleY].map((v) => addUnit(v, '')).join(',')})`
      );
    }
  }

  if (isNumber(rotate)) {
    transforms.push(`rotate(${addUnit(rotate, 'deg')})`);
  }

  return transforms;
}

/**
 * Convert VaporAnimation transform shorthands into a standard
 * set of keyframes, compatible with WAAPI. If motion eventually
 * supports hardware accleration w/ individual transform values
 * we may be able to remove this.
 */

export function convertTransformKeyframes(frame: AnimationKeyframe): Keyframe {
  if (frame.transform) {
    return frame;
  }

  const transforms = getTransformStyle(frame);

  if (transforms.length === 0) {
    return frame;
  }

  const frameWithoutXYZ = omitTransformShorthands(frame);
  // eslint-disable-next-line
  frameWithoutXYZ['transform'] = transforms.join(' ');
  return frameWithoutXYZ;
}

/**
 * Add a unit to a value
 */

type Value = number | string;

const addUnit = (value: Value, unit: string): string | 0 =>
  isNumber(value) && value !== 0 ? value + unit : value || 0;

export function removeOptionOverrides(
  keyframes: AnimationKeyframeWithOptions
): Omit<AnimationKeyframeWithOptions, 'delay' | 'easing' | 'duration'> {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { delay, easing, duration, ...result } = keyframes;
  return result;
}

function omitTransformShorthands(
  keyframes: AnimationKeyframe
): Omit<
  AnimationKeyframe,
  'x' | 'y' | 'z' | 'scale' | 'scaleX' | 'scaleY' | 'rotate'
> {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { x, y, z, scale, scaleX, scaleY, rotate, ...result } = keyframes;
  return result;
}
